<template>
  <v-card>
    <v-card-title>
      <div class="text-h6 align-baseline text-center full-width">
        <strong>{{ node.name }}</strong>
        <span class="body-1">
          &nbsp;is of type
          <span class="font-weight-medium">
            {{ useFriendlyTypeName(node.meta.type) }}
          </span>
        </span>
        <v-btn icon class="close-button elevation-0" @click="cancelDialog">
          <v-icon>mdi-close-circle</v-icon>
        </v-btn>
        <v-divider class="my-2"></v-divider>
      </div>
    </v-card-title>
    <v-card-text>
      <v-form class="mx-2">
        <v-row v-if="node.cat_id">
          <v-col cols="12">
            <v-text-field
              label=" Category Name"
              v-model="newNode.name"
              :rules="[() => !!newNode.name || 'An entry is required']"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <TypeCreator
              :node="newNode"
              :selectedRecord="selectedRecord"
              @validation="canSubmit = $event"
            />
          </v-col>
        </v-row>
        <v-row v-if="node.parent">
          <v-col>
            <conditionalCreator
              :node="newNode"
              :parentNodeId="node.parent"
              :selectedRecord="selectedRecord"
            />
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>
    <v-card-actions class="pa-4 justify-end">
      <v-btn color="primary" @click="submitChanges" :disabled="!canSubmit">
        <v-progress-circular
          v-if="isSubmitting"
          class="mr-3"
          indeterminate
          :size="20"
          :width="1"
          color="secondary"
        ></v-progress-circular>
        Update
      </v-btn>
    </v-card-actions>
  </v-card>
</template>
<script>
import { mapGetters } from "vuex";
import TypeCreator from "../../components/TypeCreator.vue";
import conditionalCreator from "../record-navigator/add-categories/conditionalCreator.vue";
import cloneDeep from "lodash/cloneDeep";

export default {
  props: {
    node: Object,
    selectedRecord: Object
  },
  components: { TypeCreator, conditionalCreator },
  name: "EditCategory",
  created() {
    const fakeNode = {
      name: this.node.name,
      meta: cloneDeep(this.node.meta)
    };
    const uom = this.units.find(item => item.uom === fakeNode.meta.uom);
    fakeNode.meta.uom = uom;
    this.newNode = Object.assign({}, fakeNode);
  },

  data() {
    return {
      search: "",
      isSubmitting: false,
      canSubmit: false,
      newNode: {},
      allowedTypes: [
        { name: "Title", type: "title" },
        { name: "Text", type: "text" },
        { name: "Yes/No", type: "boolean" },
        { name: "Numeric", type: "numeric" },
        { name: "Numeric Scale", type: "numeric-scale" },
        { name: "Numeric Array", type: "numeric-array" },
        { name: "Text Array", type: "text-array" },
        { name: "Single Choice List", type: "value-list" },
        { name: "Multiple Value List", type: "multiple-value-list" },
        { name: "Time", type: "time" },
        { name: "Date", type: "np-date" },
        { name: "Non Periodic Text", type: "np-text" }
      ]
    };
  },
  computed: {
    ...mapGetters("allTypes", { allTypes: "list" }, { root: true }),
    ...mapGetters("allUoms", { units: "list" }, { root: true })
  },
  methods: {
    cancelDialog() {
      // Put up an "Are you sure?" dialog of some sort
      if (!window.confirm("Are you sure you want to cancel?")) {
        return;
      }
      this.$emit("cancel-edit-dialog");
    },
    async submitChanges() {
      this.isSubmitting = true;
      if (this.newNode.meta.uom?.new) {
        // We have to wait for the new Unit of Measure to get added before we procceed
        this.addNewUoms();
      }
      // Use just the uom name
      if (this.newNode.meta.uom?.uom) {
        this.newNode.meta.uom = this.newNode.meta.uom.uom;
      }

      // Update the details
      // Now update the category
      this.node.meta = this.newNode.meta;
      // Update the category name
      if (this.node.name !== this.newNode.name) {
        // Update tab name if this record is open in a tab
        const params = {
          recordItemId: this.node._id,
          newText: this.newNode.name
        };
        this.$store.commit("UPDATE_TAB_TEXT", params);
        this.node.name = this.newNode.name;
      }

      const payload = {
        obj: this.node,
        root_id: this.selectedRecord._id
      };
      const result = await this.$store.dispatch(
        "updateRecNode/updateRecordNode",
        payload
      );

      this.$store.dispatch("addNotification", {
        type: result ? "success" : "errorMessage",
        message: `${result ? "Updated" : "Could not update"} category ${
          this.node.name
        } `
      });
      if (result) {
        // Update the store with the new node data
        await this.$store.dispatch("updateRecordFromData", result);
      }
      this.isSubmitting = false;
      this.$emit("cancel-edit-dialog");
    },
    async addNewUoms() {
      // Create the new unit of measure
      const payload = {
        name: this.newNode.meta.uom.uom,
        ...this.newNode.meta.uom
      };
      delete payload.new;
      // Create the new uom
      const result = await this.$store.dispatch(
        "postNewUom/createUom",
        payload
      );
      this.$store.dispatch("addNotification", {
        type: result ? "success" : "errorMessage",
        message: `${result ? "Added" : "Could not add"} new unit of measure ${
          payload.uom
        } `
      });
      if (result) {
        // Update the store with the new uom
        this.$store.commit("allUoms/updateUoms", result);
      }
    },
    useFriendlyTypeName(type) {
      const theType = this.allowedTypes.find(item => item.type === type);
      return theType.name;
    }
  }
};
</script>

<style scoped lang="scss">
.close-button {
  position: absolute;
  right: 0px;
  cursor: pointer;
  z-index: 200;
  margin-top: -10px;
}
.v-stepper__content {
  padding-top: 0;
}
.full-width {
  width: 100%;
}
</style>
